import { skipToken } from '@reduxjs/toolkit/query';
import { ArrowLeft, MapPin, Pencil, Plus, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { API_HOST } from '@/constants';
import { useTenant } from '@/hooks/use-tenant';
import { getInitials } from '@/lib/utils';
import { AvatarEditor } from '@/components/avatar-editor';
import {
  useGetClientOrganizationsQuery,
  useGetClientScopeQuery,
  useGetOrganizationProfileQuery,
  useGetScopeOptionsQuery,
  useRemoveClientScopeMutation,
  useUpdateClientScopeMutation,
  useUpdateOrganizationProfileMutation,
  useUpdateOrganizationTemplateMutation,
} from '@/state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

type MediaType = 'banner' | 'logo';

type CountryApiResponse = {
  data?: Array<{ name: string }>;
};

type ProfileFormState = {
  name: string;
  slogan: string;
  address: string;
  email: string;
  website: string;
  about_us: string;
  telephone: string;
  country: string;
};

const ManageOrganization = () => {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isScopeDialogOpen, setIsScopeDialogOpen] = useState(false);
  const [mediaDialog, setMediaDialog] = useState<{
    showEditor: boolean;
    type: MediaType;
    file: File | null;
    image: string;
  }>({
    showEditor: false,
    type: 'banner',
    file: null,
    image: '',
  });
  const [selectedScopeIds, setSelectedScopeIds] = useState<number[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [isCountriesLoading, setIsCountriesLoading] = useState(false);
  const [profileForm, setProfileForm] = useState<ProfileFormState>({
    name: '',
    slogan: '',
    address: '',
    email: '',
    website: '',
    about_us: '',
    telephone: '',
    country: '',
  });
  const [nameError, setNameError] = useState('');

  const { data: organizations = [] } = useGetClientOrganizationsQuery();
  const currentOrganization = useMemo(
    () => organizations.find((org) => org.client.schema_name === tenant),
    [organizations, tenant]
  );
  const clientId = currentOrganization?.client?.id;

  const {
    data: profile,
    isLoading: isProfileLoading,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useGetOrganizationProfileQuery(tenant ? { tenant } : skipToken);
  const {
    data: scope,
    isLoading: isScopeLoading,
    refetch: refetchScope,
  } = useGetClientScopeQuery(
    tenant && clientId ? { tenant, clientId } : skipToken
  );
  const { data: scopeOptions = [] } = useGetScopeOptionsQuery(
    tenant && isScopeDialogOpen ? { tenant } : skipToken
  );
  const [updateTemplate] = useUpdateOrganizationTemplateMutation();
  const [updateOrganizationProfile, { isLoading: isUpdatingProfile }] =
    useUpdateOrganizationProfileMutation();
  const [updateClientScope, { isLoading: isUpdatingScope }] =
    useUpdateClientScopeMutation();
  const [removeClientScope, { isLoading: isRemovingScope }] =
    useRemoveClientScopeMutation();

  const isLoading = isProfileLoading || isScopeLoading;
  const bannerUrl = withServerUrl(scope?.banner_url) ||
    '/images/static-images/course-banner.jpg';
  const logoUrl = withServerUrl(profile?.logo_url || scope?.logo_url || null);
  const organizationName = profile?.name || scope?.name || 'Organization';
  const aboutUs = profile?.about_us || 'No description available.';
  const scopes = scope?.client_scope ?? [];
  const existingScopeIds = new Set(scopes.map((scopeItem) => scopeItem.id));
  const selectableScopeOptions = scopeOptions.filter(
    (scopeOption) => !existingScopeIds.has(scopeOption.id)
  );

  useEffect(() => {
    if (!isProfileDialogOpen || countries.length > 0) return;

    const controller = new AbortController();
    const fetchCountries = async () => {
      try {
        setIsCountriesLoading(true);
        const response = await fetch(
          'https://countriesnow.space/api/v0.1/countries/positions',
          {
            method: 'GET',
            signal: controller.signal,
          }
        );
        const result = (await response.json()) as CountryApiResponse;
        const countryNames = (result.data ?? [])
          .map((country) => country.name)
          .filter(Boolean);
        setCountries(countryNames);
      } catch {
        setCountries([]);
      } finally {
        setIsCountriesLoading(false);
      }
    };

    void fetchCountries();
    return () => controller.abort();
  }, [isProfileDialogOpen, countries.length]);

  const openProfileDialog = () => {
    if (!profile) return;
    setProfileForm({
      name: profile.name ?? '',
      slogan: profile.slogan ?? '',
      address: profile.address ?? '',
      email: profile.email ?? '',
      website: profile.website ?? '',
      about_us: profile.about_us ?? '',
      telephone: profile.telephone ?? '',
      country: profile.country ?? '',
    });
    setNameError('');
    setIsProfileDialogOpen(true);
  };

  const openMediaDialog = (type: MediaType) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (!target?.files?.[0]) return;
      const file = target.files[0];
      const imageUrl = URL.createObjectURL(file);
      setMediaDialog({
        showEditor: true,
        type,
        file,
        image: imageUrl,
      });
    };
    fileInput.click();
  };

  const handleMediaSubmit = async (_: string, file: File) => {
    if (!tenant) return;
    const payload = new FormData();
    payload.append(
      mediaDialog.type === 'banner' ? 'banner_url' : 'logo_url',
      file
    );

    try {
      await updateTemplate({ tenant, data: payload }).unwrap();
      toast.success(
        `${mediaDialog.type === 'banner' ? 'Banner' : 'Logo'} updated successfully`
      );
      if (mediaDialog.image) URL.revokeObjectURL(mediaDialog.image);
      setMediaDialog((prev) => ({
        ...prev,
        showEditor: false,
        image: '',
        file: null,
      }));
      await Promise.all([refetchProfile(), refetchScope()]);
    } catch {
      toast.error('Failed to update media');
    }
  };

  const handleRemoveScope = async (scopeId: number) => {
    if (!tenant || !clientId) return;

    try {
      await removeClientScope({
        tenant,
        clientId,
        data: {
          client_scope: [{ add_id: scopeId }],
        },
      }).unwrap();
      toast.success('Scope removed');
    } catch {
      toast.error('Failed to remove scope');
    }
  };

  const handleAddScopes = async () => {
    if (!tenant || !clientId || selectedScopeIds.length === 0) return;

    try {
      await updateClientScope({
        tenant,
        clientId,
        data: {
          client_scope: selectedScopeIds.map((id) => ({ add_id: id })),
        },
      }).unwrap();
      toast.success('Scopes updated');
      setSelectedScopeIds([]);
      setIsScopeDialogOpen(false);
    } catch {
      toast.error('Failed to update scopes');
    }
  };

  const handleProfileSubmit = async () => {
    if (!tenant) return;
    if (!profileForm.name.trim()) {
      setNameError('Name is required');
      return;
    }

    try {
      await updateOrganizationProfile({
        tenant,
        data: {
          name: profileForm.name.trim(),
          slogan: profileForm.slogan,
          address: profileForm.address,
          email: profileForm.email,
          website: profileForm.website,
          about_us: profileForm.about_us,
          telephone: profileForm.telephone,
          country: profileForm.country,
        },
      }).unwrap();
      toast.success('Company profile updated successfully');
      setIsProfileDialogOpen(false);
      await refetchProfile();
    } catch {
      toast.error('Failed to update company profile');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (isProfileError || !profile) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          className="mb-4 gap-2 pl-0 hover:bg-transparent hover:text-primary"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <p className="text-sm text-muted-foreground">
          Organization profile is not available.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-screen-2xl mx-auto">
      <Button
        variant="ghost"
        className="gap-2 pl-0 hover:bg-transparent hover:text-primary"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </Button>

      <section className="rounded-xl border bg-card shadow-sm overflow-hidden">
        <div className="relative h-44 md:h-60">
          <img
            src={bannerUrl}
            alt="Organization banner"
            className="h-full w-full object-cover"
          />
          <button
            type="button"
            className="absolute top-3 right-3 rounded-full bg-primary text-primary-foreground p-2"
            onClick={() => openMediaDialog('banner')}
          >
            <Pencil className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 pb-6 relative">
          <div className="-mt-14 mb-4 flex items-end justify-between gap-3">
            <div className="relative">
              <div className="h-28 w-28 rounded-full border-4 border-background bg-slate-700 text-white flex items-center justify-center text-5xl font-semibold">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt={organizationName}
                    className="h-full w-full rounded-full object-cover"
                  />
                ) : (
                  getInitials(organizationName).slice(0, 1) || 'O'
                )}
              </div>
              <button
                type="button"
                className="absolute bottom-0 right-0 rounded-full bg-primary text-primary-foreground p-1.5"
                onClick={() => openMediaDialog('logo')}
              >
                <Pencil className="h-3 w-3" />
              </button>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-primary/15 text-primary px-4 py-2 text-sm font-medium"
              onClick={openProfileDialog}
            >
              <Pencil className="h-4 w-4" />
              Edit
            </button>
          </div>

          <h1 className="text-3xl font-semibold text-slate-700 dark:text-white">{organizationName}</h1>

          <div className="mt-2 flex items-center gap-2 text-xl text-slate-500">
            <MapPin className="h-4 w-4" />
            <span>{profile.address || 'Address not available'}</span>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {scopes.map((scopeItem) => (
              <span
                key={scopeItem.id}
                className="inline-flex items-center gap-2 rounded-md bg-slate-200 px-3 py-1 text-sm text-slate-700"
              >
                {scopeItem.name}
                <button
                  type="button"
                  className="text-base leading-none"
                  onClick={() => handleRemoveScope(scopeItem.id)}
                  disabled={isRemovingScope}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
            <button
              type="button"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-slate-200 text-slate-700"
              onClick={() => setIsScopeDialogOpen(true)}
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="border-b">
        <div className="inline-flex items-center gap-2 px-3 py-2 text-primary border-b-2 border-primary">
          <span className="h-3.5 w-3.5 rounded-sm bg-primary/20" />
          <span className="font-medium">About us</span>
        </div>
      </section>

      <section className="rounded-xl border bg-card p-5 text-xl text-slate-700 dark:text-white shadow-sm">
        {aboutUs}
      </section>

      <AvatarEditor
        open={mediaDialog.showEditor}
        image={mediaDialog.image}
        borderRadius={mediaDialog.type === 'logo' ? 100 : 0}
        aspectRatio={mediaDialog.type === 'logo' ? 1 : 1.5}
        onClose={() => {
          if (mediaDialog.image) URL.revokeObjectURL(mediaDialog.image);
          setMediaDialog((prev) => ({
            ...prev,
            showEditor: false,
            image: '',
            file: null,
          }));
        }}
        onSave={(croppedImage, file) => {
          void handleMediaSubmit(croppedImage, file);
        }}
      />

      <Dialog open={isScopeDialogOpen} onOpenChange={setIsScopeDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Select scopes</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-auto pr-1">
            {selectableScopeOptions.length === 0 ? (
              <p className="text-sm text-muted-foreground">No scope options available.</p>
            ) : (
              selectableScopeOptions.map((scopeOption) => (
                <label
                  key={scopeOption.id}
                  className="flex items-center gap-3 rounded-md border p-3"
                >
                  <Checkbox
                    checked={selectedScopeIds.includes(scopeOption.id)}
                    onCheckedChange={(checked) => {
                      setSelectedScopeIds((prev) => {
                        if (checked) return [...prev, scopeOption.id];
                        return prev.filter((id) => id !== scopeOption.id);
                      });
                    }}
                  />
                  <span className="text-sm">{scopeOption.name}</span>
                </label>
              ))
            )}
          </div>
          <div className="flex justify-end">
            <Button
              onClick={() => {
                void handleAddScopes();
              }}
              disabled={selectedScopeIds.length === 0 || isUpdatingScope}
            >
              {isUpdatingScope ? 'Submitting...' : 'Submit'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isProfileDialogOpen} onOpenChange={setIsProfileDialogOpen}>
        <DialogContent className="sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>Edit company profile</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="org-name" className="mb-2 block">Name *</Label>
              <Input
                id="org-name"
                value={profileForm.name}
                onChange={(event) => {
                  setProfileForm((prev) => ({ ...prev, name: event.target.value }));
                  if (nameError) setNameError('');
                }}
              />
              {nameError ? <p className="text-xs text-red-500 mt-1">{nameError}</p> : null}
            </div>

            <div>
              <Label htmlFor="org-slogan" className="mb-2 block">Slogan</Label>
              <Input
                id="org-slogan"
                value={profileForm.slogan}
                onChange={(event) =>
                  setProfileForm((prev) => ({ ...prev, slogan: event.target.value }))
                }
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="org-country" className="mb-2 block">Country</Label>
                <Select
                  value={profileForm.country || undefined}
                  onValueChange={(value) =>
                    setProfileForm((prev) => ({ ...prev, country: value }))
                  }
                >
                  <SelectTrigger id="org-country" className="w-full">
                    <SelectValue
                      placeholder={
                        isCountriesLoading ? 'Loading countries...' : 'Country'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {countries.map((country) => (
                      <SelectItem key={country} value={country}>
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="org-address" className="mb-2 block">Address</Label>
                <Input
                  id="org-address"
                  value={profileForm.address}
                  onChange={(event) =>
                    setProfileForm((prev) => ({ ...prev, address: event.target.value }))
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="org-email" className="mb-2 block">Email</Label>
                <Input
                  id="org-email"
                  value={profileForm.email}
                  onChange={(event) =>
                    setProfileForm((prev) => ({ ...prev, email: event.target.value }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="org-phone" className="mb-2 block">Contact number</Label>
                <Input
                  id="org-phone"
                  value={profileForm.telephone}
                  onChange={(event) =>
                    setProfileForm((prev) => ({ ...prev, telephone: event.target.value }))
                  }
                />
              </div>
            </div>

            <div>
              <Label htmlFor="org-website" className="mb-2 block">Website</Label>
              <Input
                id="org-website"
                value={profileForm.website}
                onChange={(event) =>
                  setProfileForm((prev) => ({ ...prev, website: event.target.value }))
                }
              />
            </div>

            <div>
              <Label htmlFor="org-about" className="mb-2 block">About us</Label>
              <Textarea
                id="org-about"
                value={profileForm.about_us}
                className="min-h-28"
                onChange={(event) =>
                  setProfileForm((prev) => ({ ...prev, about_us: event.target.value }))
                }
              />
            </div>

            <div className="flex justify-end">
              <Button
                onClick={() => {
                  void handleProfileSubmit();
                }}
                disabled={isUpdatingProfile}
              >
                {isUpdatingProfile ? 'Updating...' : 'Update'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManageOrganization;
  const withServerUrl = (url?: string | null) => {
    if (!url) return null;
    if (/^https?:\/\//i.test(url)) return url;
    if (!url.startsWith('/')) return `${API_HOST}/${url}`;
    return `${API_HOST}${url}`;
  };
